import React, { Component } from "react";
import { Col, Table, Row, Select, notification } from "antd";
import { ContentWrapper, Icon } from "components";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { formatPrice, getSuccessLevelBadge } from "utils/utils";
import moment from "moment";
import { push } from "connected-react-router";
import { Area } from "@ant-design/charts";
import { getColumnFilter } from "utils/comp-utils";

class Depth extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			data: [],
			startDate: moment().add({ months: -5 }).startOf("month"),
			endDate: moment().endOf("month")
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		await this.getData();
		this.breadcrumb();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData() {
		const { startDate, endDate } = this.state;
		const { dispatch, countries } = this.props;

		dispatch(setLoader(true));

		const body = {
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString(),
			type: "depth"
		};

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("search-by-type"),
				data: body
			});

			if (response.ok) {
				const { commissions = [], cards, commissionsChart, eligible, partner } = response.data.results;

				const commissionsParsed = commissions.map((entry: any) => {
					const countryCallingCode = entry.origin.phone?.substring(0, 4).replace("+", "");
					let country;

					if (countryCallingCode) {
						const countryObj = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCode));
						country = {
							name: countryObj?.name,
							alpha2Code: countryObj?.alpha2Code
						};
					}

					return { ...entry, origin: { ...entry.origin, country } };
				});

				this.setState({
					data: commissionsParsed,
					cards,
					commissionsChart,
					eligible,
					partnerDetail: partner
				});
			} else {
				notification.error({
					message: strings.commissions.depth,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async getUsers() {
		const { partnerSearch } = this.state;

		try {
			const body = { search: partnerSearch, page: 0, perPage: 10 };

			const response = await API.post({
				url: Endpoints.uriPartners("search"),
				data: body
			});

			if (response.ok) {
				const { users = [] } = response.data.results || {};
				this.setState({ partnerOptions: users });
			} else {
				notification.error({
					message: strings.commissions.depth,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate, partnerOptions } = this.state;

				return {
					locations: [
						{
							text: strings.commissions.depth,
							icon: "organization"
						}
					],
					actions: [
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	renderMovements() {
		const { data = [], partner } = this.state;
		const { countries } = this.props;

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.commissions.depth}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<p>
							{strings.formatString(
								strings.wallet.earnedPeriod,
								formatPrice(data.reduce((acc: number, entry: any) => acc + entry.value, 0))
							)}
						</p>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: "max-content" }}
					columns={[
						{
							title: strings.fields.date,
							dataIndex: "date",
							key: "date",
							render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
							width: 150
						},
						{
							title: strings.fields.country,
							dataIndex: "origin",
							key: "originCountry",
							sorter: (a: any, b: any) => {
								return a?.country?.name.localeCompare(b?.country?.name);
							},
							render: (origin: any) => {
								return (
									<span className="TableRowAligned">
										<div className="react-tel-input">
											<div className="selected-flag">
												<span className={`flag ${origin?.country?.alpha2Code?.toLowerCase()}`} />
											</div>
										</div>
										{origin?.country?.name}
									</span>
								);
							},
							...getColumnFilter(["origin.country.name", "origin.country.alpha2Code"])
						},
						{
							title: strings.generic.from,
							dataIndex: "origin",
							key: "origin",
							render: (origin: any) => {
								if (!origin) return "-";

								return (
									<span className="TableRowAligned">
										{origin.name}
										{Boolean(origin?.userCode) ? ` (#${origin.userCode})` : ""}
									</span>
								);
							},
							sorter: (a, b) => a.origin.name?.localeCompare(b.origin.name),
							...getColumnFilter(["origin.name", "origin.userCode", "origin.email"])
						},
						{
							title: strings.commissions.depthLevel,
							dataIndex: "depth",
							sorter: (a, b) => a.depth - b.depth,
							...getColumnFilter("depth")
						},
						{
							title: strings.users.partnerRanking,
							dataIndex: "actualSuccessLevel",
							key: "actualSuccessLevel",
							render: (_: string, record: any) => getSuccessLevelBadge(record?.binaryPartner?.partnerSuccessLevel) || "-",
							sorter: (a, b) => a?.binaryPartner?.partnerSuccessLevel?.localeCompare(b?.binaryPartner?.partnerSuccessLevel),
							...getColumnFilter(["binaryPartner.partnerSuccessLevel"])
						},
						{
							title: strings.commissions.value,
							dataIndex: "value",
							key: "value",
							render: (value: number) => formatPrice(value),
							sorter: (a, b) => a.value - b.value,
							width: 150,
						},
						{
							title: strings.generic.expirationDate,
							dataIndex: "expirationDate",
							key: "expirationDate",
							render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
							align: "center",
							sorter: (a, b) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
							width: 150
						}
					]}
					dataSource={data}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	renderChart() {
		const { commissionsChart = [] } = this.state;

		const config = {
			data: commissionsChart,
			xField: "month",
			yField: "depth",
			appendPadding: 10,
			smooth: true,
			animation: {
				appear: {
					animation: "path-in",
					duration: 0
				}
			},
			meta: {
				depth: {
					alias: strings.wallet.totalBilled
				}
			},
			tooltip: {
				formatter: (datum: any) => {
					return { name: strings.wallet.totalBilled, value: formatPrice(datum.depth) };
				}
			}
		};

		return (
			<ContentWrapper extraStyle={{ marginBottom: 0 }}>
				<div className="ScreenHeader">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.wallet.monthlyControl}</h2>
					</div>
				</div>
				<Area style={{ marginTop: 20 }} {...config} />
			</ContentWrapper>
		);
	}

	render() {
		const { cards, eligible, partnerDetail } = this.state;
		const { totalDepth = 0, available = 0, spent = 0, expired = 0 } = cards || {};

		const CARDS = [
			{
				icon: "wallet",
				value: totalDepth,
				description: strings.commissions.withThisBonus,
				type: "currency"
			},
			{
				icon: "wallet",
				value: available,
				description: strings.wallet.available,
				type: "currency"
			},
			{
				icon: "wallet",
				value: spent,
				description: strings.wallet.spent,
				type: "currency"
			},
			{
				icon: "wallet",
				value: expired,
				description: strings.wallet.expired,
				type: "currency"
			}
		];

		return (
			<div className="CommissionDepth">
				<Helmet>
					<title>{strings.commissions.depth}</title>
					<meta name="description" content="Description of commission depth" />
				</Helmet>
				{!eligible && (
					<Col xs={24}>
						<ContentWrapper extraStyle={{ backgroundColor: "#dce0dd" }}>
							<div className="ScreenHeader">
								<div className="ScreenHeaderLeft">
									<Icon name="user" />
									<h2>{strings.commissions.eligibility}</h2>
								</div>
							</div>
							<p>
								<strong>
									{strings.commissions.actualRank}: {getSuccessLevelBadge(partnerDetail?.partner?.successLevel)}
								</strong>
							</p>
							<p className="CommissionsPartnersAttention">{strings.commissions.notEligibleBronze}</p>
						</ContentWrapper>
					</Col>
				)}
				<div className="IconCards">
					<Row gutter={[20, 20]}>
						{CARDS.map((card, index) => (
							<Col xs={24} md={6} key={index}>
								<div className="IconCardOuterContainer">
									<div className="IconCard">
										<div className="IconCardHeader">
											<div className="IconCardHeaderMask" />
											<div className="IconCardType">
												<Icon name={card.icon} />
											</div>
										</div>
										<div className="IconCardBody">
											<span className="IconCardValue">
												{card.type === "currency" ? formatPrice(card.value) : card.value}
											</span>
											<h3 className="IconCardDescription">{card.description}</h3>
										</div>
									</div>
								</div>
							</Col>
						))}
						<Col xs={24}>{this.renderChart()}</Col>
						<Col xs={24}>{this.renderMovements()}</Col>
					</Row>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(Depth);
